import {
  getProfileDetails,
  getProfileFunding,
  getProfileObjective,
  putProfileDetails,
  putProfileFunding,
  putProfileObjective,
} from "../../api/profile";
import {
  createEmptyOnboardingProfile,
  mapProfileToOnboarding,
  mergeOnboardingIntoDetails,
  mergeOnboardingIntoFunding,
  mergeOnboardingIntoObjective,
} from "./mappers";
import type { OnboardingProfileData, OnboardingRecord } from "./types";

const STORAGE_PREFIX = "roott_onboarding_v2";

const nowIso = () => new Date().toISOString();

export function getUserScopedKey(userKey: string) {
  return `${STORAGE_PREFIX}:${userKey}`;
}

export function readOnboardingRecord(userKey: string): OnboardingRecord | null {
  try {
    const raw = localStorage.getItem(getUserScopedKey(userKey));
    return raw ? (JSON.parse(raw) as OnboardingRecord) : null;
  } catch {
    return null;
  }
}

export function writeOnboardingRecord(userKey: string, record: OnboardingRecord) {
  localStorage.setItem(getUserScopedKey(userKey), JSON.stringify(record));
}

export function saveOnboardingDraft(userKey: string, data: OnboardingProfileData) {
  const existing = readOnboardingRecord(userKey);
  writeOnboardingRecord(userKey, {
    completed: existing?.completed ?? false,
    completedAt: existing?.completedAt,
    lastUpdatedAt: nowIso(),
    data,
  });
}

export function updateOnboardingDraft(
  userKey: string,
  patch: Partial<OnboardingProfileData>
) {
  const existing = readOnboardingRecord(userKey);
  const merged: OnboardingProfileData = {
    ...createEmptyOnboardingProfile(),
    ...(existing?.data ?? {}),
    ...patch,
  };
  saveOnboardingDraft(userKey, merged);
  return merged;
}

export async function resolveOnboardingRecord(userKey: string): Promise<OnboardingRecord> {
  const existing = readOnboardingRecord(userKey);
  if (existing) {
    return existing;
  }

  try {
    const [detailsRes, objectiveRes, fundingRes] = await Promise.allSettled([
      getProfileDetails(),
      getProfileObjective(),
      getProfileFunding(),
    ]);

    const details = detailsRes.status === "fulfilled" ? detailsRes.value : null;
    const objective = objectiveRes.status === "fulfilled" ? objectiveRes.value : null;
    const funding = fundingRes.status === "fulfilled" ? fundingRes.value : null;

    const inferred = {
      ...createEmptyOnboardingProfile(),
      ...mapProfileToOnboarding(details, objective, funding),
    };

    const hasMeaningfulProfile = Boolean(
      inferred.organizationName ||
        inferred.whatWeDo ||
        inferred.country ||
        inferred.postcode ||
        inferred.focusThemes.length ||
        inferred.fundingTypes.length
    );

    const record: OnboardingRecord = {
      completed: hasMeaningfulProfile,
      completedAt: hasMeaningfulProfile ? nowIso() : undefined,
      lastUpdatedAt: nowIso(),
      data: inferred,
    };

    writeOnboardingRecord(userKey, record);
    return record;
  } catch {
    const record: OnboardingRecord = {
      completed: false,
      lastUpdatedAt: nowIso(),
      data: createEmptyOnboardingProfile(),
    };
    writeOnboardingRecord(userKey, record);
    return record;
  }
}

export async function completeOnboarding(
  userKey: string,
  data: OnboardingProfileData
): Promise<{ synced: boolean }> {
  let synced = true;
  const results = await Promise.allSettled([
    getProfileDetails(),
    getProfileObjective(),
    getProfileFunding(),
  ]);

  const details = results[0].status === "fulfilled" ? results[0].value : null;
  const objective = results[1].status === "fulfilled" ? results[1].value : null;
  const funding = results[2].status === "fulfilled" ? results[2].value : null;

  const saveResults = await Promise.allSettled([
    putProfileDetails(mergeOnboardingIntoDetails(details, data)),
    putProfileObjective(mergeOnboardingIntoObjective(objective, data) as any),
    putProfileFunding(mergeOnboardingIntoFunding(funding, data)),
  ]);

  if (saveResults.some((result) => result.status === "rejected")) {
    synced = false;
  }

  writeOnboardingRecord(userKey, {
    completed: true,
    completedAt: nowIso(),
    lastUpdatedAt: nowIso(),
    data,
  });

  return { synced };
}
